import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { IReducers } from 'app/store/reducers';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { SessionService } from 'app/store/services/session.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IUserSessionData } from 'app/store/models/user.model';
import { ApiDataSource } from 'app/datasources/api.datasrouce';
import { HttpClient } from '@angular/common/http';
import { ETableColumnType, ITableColumn } from 'app/store/models/table.model';
import { IContent, IContentItem } from 'app/store/models/content.model';
import { environment } from '../../../environments/environment';
import { ContentService } from '../../store/services/content.service';
import { MessageService } from '../../store/services/message.service';
import { openDialog, openSnackbar } from '../../store/actions/page.action';
import { TestContentComponent } from './test-content/test-content.component';

@Component({
  selector: 'app-content-info',
  templateUrl: './content-info.component.html',
  styleUrls: ['./content-info.component.scss'],
})
export class ContentInfoComponent implements OnInit, OnDestroy {
  contentGroupName: string;
  contentGroupId: number;
  tableReady: boolean;
  dataSource: ApiDataSource<IContentItem>;
  tableColumns: ITableColumn<IContentItem>[];
  userSession: IUserSessionData;
  tableDataSourceParams = {
    page: 1,
    pageSize: 10,
    sortAttr: 'content_name',
    sortDir: 'asc',
  };

  buttonObservables: { [key: number]: Subject<boolean> };

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private store: Store<IReducers>,
    private sessionService: SessionService,
    private http: HttpClient,
    private readonly contentService: ContentService,
    private readonly messageService: MessageService,
  ) {
  }

  ngOnInit() {
    this.contentGroupName = this.activatedRoute.snapshot.params.group_name;
    this.contentGroupId = this.activatedRoute.snapshot.params.id;
    this.dataSource = new ApiDataSource<IContentItem>(
      this.http,
      environment.apiUrl + `/v1/contents/groups/${this.contentGroupId}`,
    );

    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe((sessionData: IUserSessionData) => {
        this.userSession = sessionData;
        this.tableColumns = [
          {
            id: 'content_name',
            title: 'Name',
            sortable: true,
            leftAdjusted: true,
          },
          {
            id: 'content_goal',
            title: 'Goal',
            leftAdjusted: true,
            renderer: row => row.content_goal === 'face' ? 'Meeting' : row.content_goal,
          },
          {
            id: 'campaign_name',
            title: 'Campaign',
            leftAdjusted: true,
            renderer: (element: IContentItem) =>
              `${
                element.campaign_name
                  ? element.campaign_name
                  : 'No Campaign Associated'
              }`,
          },
          {
            id: 'preview',
            title: 'Preview',
            hint: 'preview',
            type: ETableColumnType.BUTTON,
            getButtonProperties: (element: IContentItem) => {
              return {
                text: 'Preview',
                route: [
                  `/contents/${element.content_id}/preview`,
                  {
                    group_name: this.contentGroupName,
                  },
                ],
                size: ButtonSize.SMALL,
                color: ButtonColor.ORANGE,
              };
            },
          },
          {
            id: 'test',
            title: 'Test',
            hint: 'test',
            type: ETableColumnType.BUTTON,
            getButtonProperties: (element: IContentItem) => {
              return {
                text: 'Test',
                size: ButtonSize.SMALL,
                color: ButtonColor.BLUE,
                buttonDisabled: () => !element.campaign_name,
                loadingObservable: this.createTestButtonObservable(
                  element.content_id,
                ),
                onClick: (el: IContentItem) => {
                  this.buttonObservables[element.content_id].next(true);
                  this.store.dispatch(
                    openDialog({
                      dialogData: {
                        title: 'Send Test Email To',
                        content: TestContentComponent,
                        componentData: {
                          contentId: el.content_id,
                          currentEmail: this.userSession.email,
                        },
                        dialogMinWidth: '800px',
                        onClose: () => {
                          this.buttonObservables[element.content_id].next(
                            false,
                          );
                        },
                        canClose: true,
                        buttons: [
                          {
                            color: ButtonColor.ORANGE,
                            text: 'Send',
                            style: 'filled',
                            onClick: data => {
                              this.sendTestEmail(
                                data.dialogForm.value.email,
                                data.dialogForm.value.content,
                              );
                              data.closeDialog();
                            },
                            size: ButtonSize.SMALL,
                          },
                        ],
                      },
                    }),
                  );
                },
              };
            },
          },
        ];
        this.tableReady = true;
      });
  }

  onTest(element: IContentItem) {
    this.store.dispatch(
      openDialog({
        dialogData: {
          title: 'Send Test Email To',
          content: TestContentComponent,
          componentData: {
            contentId: element.content_id,
            currentEmail: this.userSession.email,
          },
          dialogMinWidth: '800px',
          onClose: () => {
            this.buttonObservables[element.content_id].next(
              false,
            );
          },
          canClose: true,
          buttons: [
            {
              color: ButtonColor.ORANGE,
              text: 'Send',
              style: 'filled',
              onClick: data => {
                this.sendTestEmail(
                  data.dialogForm.value.email,
                  data.dialogForm.value.content,
                );
                data.closeDialog();
              },
              size: ButtonSize.SMALL,
            },
          ],
        },
      }),
    );
  }

  createTestButtonObservable(contentId: number) {
    if (!this.buttonObservables) {
      this.buttonObservables = { [contentId]: new Subject<boolean>() };
      return this.buttonObservables[contentId].asObservable();
    }

    if (this.buttonObservables[contentId]) {
      return this.buttonObservables[contentId].asObservable();
    }

    this.buttonObservables[contentId] = new Subject<boolean>();
    return this.buttonObservables[contentId].asObservable();
  }

  ngOnDestroy() {
    this.destroy$.next(false);
  }

  getEditButtonProperties(): Button {
    return {
      text: 'Edit Name',
      size: ButtonSize.SMALL,
      color: ButtonColor.ORANGE,
      route: [
        `/contents/content_group/${this.contentGroupId}/edit`,
        {
          groupName: this.contentGroupName,
        },
      ],
    };
  }

  getContentSign(sign: string): string {
    if (!sign) {
      return '';
    }
    return sign.replace('<strong>', '<b>').replace('</strong>', '</b>');
  }

  buildMessage(items: string[]): string {
    return items.map(i => {
      if (!RegExp.prototype.test.bind(/(<([^>]+)>)/i)(i)) {
        i = `<p>${i}</p>`;
      }

      return i;
    }).join('');
  }

  private sendTestEmail(email, content: IContent) {
    let message: string;

    switch (content.content_template) {
      case 'email_template1':
        message = this.buildMessage([
          content.content_attention || '',
          content.first_benefit || '',
          content.second_benefit || '',
          content.content_closing || '',
          this.getContentSign(content.content_sign),
          content.content_details || '',
        ]);
        break;
      case 'email_template2':
        message = this.buildMessage([
          content.content_opening || '',
          content.second_benefit || '',
          content.content_attention || '',
          content.first_benefit || '',
          content.content_closing || '',
          this.getContentSign(content.content_sign),
          content.content_details || '',
        ]);
        break;
      case 'email_template3':
        message = this.buildMessage([
          content.content_opening || '',
          content.first_benefit || '',
          content.second_benefit || '',
          content.content_closing || '',
          this.getContentSign(content.content_sign),
          content.content_details || '',
        ]);
        break;
      case 'email_template4':
        message = this.buildMessage([
          content.content_opening || '',
          content.first_benefit || '',
          content.content_attention || '',
          content.content_closing || '',
          this.getContentSign(content.content_sign),
          content.content_details || '',
        ]);
        break;
      default:
        message = this.buildMessage([
          content.content_attention,
          content.first_benefit,
          content.second_benefit,
          content.content_closing,
          this.getContentSign(content.content_sign),
          content.content_details || '',
        ]);
        break;
    }

    this.messageService
      .sendTestMessage(
        email,
        message,
        content.subject,
        content.greeting,
        this.userSession.first_name,
      )
      .subscribe(result => {
        if (result && result.success) {
          this.store.dispatch(
            openSnackbar({
              snackbarData: {
                data: {
                  message: 'Test email message was send to email: ' + email,
                  type: 'success',
                },
                verticalPosition: 'bottom',
              },
            }),
          );
        }
        this.buttonObservables[content.content_id].complete();
      });
  }
}
